/**
 * Email preferences (Mar 2026) — per-stream toggles on Settings so users
 * can mute the ONE email stream that annoys them instead of nuking all
 * TruckTrackr email via the global unsubscribe link.
 *
 * Streams (backed by /api/me/email-preferences):
 *   digests         → weekly digest emails
 *   product_updates → announcements / tips / nudges / campaigns
 *   event_requests  → owners only: event-inquiry leads + matching
 *
 * If the user is on the master do-not-email list (clicked the global
 * unsubscribe link), a banner explains it and offers one-click
 * re-subscribe; the toggles are disabled until then.
 */
import { useEffect, useState } from "react";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import { EnvelopeSimple, Warning } from "@phosphor-icons/react";
import { Button } from "./ui/button";

const ROWS = [
  {
    key: "digests",
    title: "Weekly digest",
    desc: "Your weekly TruckTrackr summary email.",
  },
  {
    key: "product_updates",
    title: "Product updates & tips",
    desc: "New features, announcements, and reminders to keep your info fresh.",
  },
  {
    key: "event_requests",
    title: "Event requests",
    desc: "Hosts looking to book a food truck for their event. Turning this off removes you from event matching.",
    ownerOnly: true,
  },
];

export const EmailPrefsCard = () => {
  const [prefs, setPrefs] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/me/email-preferences");
        setPrefs(data);
      } catch {
        // Leave as null — card renders a quiet loading state
      }
    })();
  }, []);

  const toggle = async (key) => {
    const next = { ...prefs, [key]: !prefs[key] };
    setPrefs(next); // optimistic
    setBusy(true);
    try {
      const { data } = await api.put("/me/email-preferences", { [key]: next[key] });
      setPrefs(data);
    } catch (e) {
      setPrefs(prefs); // revert
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't save — try again.");
    } finally {
      setBusy(false);
    }
  };

  const resubscribeAll = async () => {
    setBusy(true);
    try {
      const { data } = await api.put("/me/email-preferences", { resubscribe_all: true });
      setPrefs(data);
      toast.success("You're back on the list — emails will resume.");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't re-subscribe — try again.");
    } finally {
      setBusy(false);
    }
  };

  if (!prefs) {
    return (
      <section className="rounded-2xl border border-border bg-card p-5" data-testid="settings-email-prefs">
        <h2 className="font-display font-bold text-base">Email preferences</h2>
        <p className="text-sm text-muted-foreground mt-1">Loading…</p>
      </section>
    );
  }

  const rows = ROWS.filter((r) => !r.ownerOnly || prefs.role === "owner");
  const blocked = !!prefs.globally_unsubscribed;

  return (
    <section className="rounded-2xl border border-border bg-card p-5 space-y-4" data-testid="settings-email-prefs">
      <header>
        <h2 className="font-display font-bold text-base inline-flex items-center gap-2">
          <EnvelopeSimple weight="duotone" className="h-5 w-5 text-primary" />
          Email preferences
        </h2>
        <p className="text-xs text-muted-foreground mt-0.5">
          Choose which TruckTrackr emails you want. Transactional emails
          (password resets, staff invites) always deliver.
        </p>
      </header>

      {blocked && (
        <div
          className="rounded-xl border border-amber-300 bg-amber-50 dark:bg-amber-950/30 dark:border-amber-800 p-3 flex items-start gap-2"
          data-testid="email-prefs-suppressed-banner"
        >
          <Warning weight="fill" className="h-4 w-4 text-amber-600 mt-0.5 flex-none" />
          <div className="text-xs text-amber-800 dark:text-amber-300 flex-1">
            <span className="font-semibold">You're unsubscribed from all TruckTrackr emails.</span>{" "}
            Re-subscribe to manage individual streams below.
          </div>
          <Button
            size="sm"
            variant="outline"
            onClick={resubscribeAll}
            disabled={busy}
            data-testid="email-prefs-resubscribe-btn"
            className="flex-none border-amber-400 text-amber-800 dark:text-amber-300 hover:bg-amber-100"
          >
            Re-subscribe
          </Button>
        </div>
      )}

      <div className={`space-y-2 ${blocked ? "opacity-50 pointer-events-none" : ""}`}>
        {rows.map((row) => (
          <label
            key={row.key}
            className="flex items-start justify-between gap-3 rounded-xl border border-border bg-muted/10 p-3 cursor-pointer select-none"
            data-testid={`email-pref-${row.key}`}
          >
            <div className="min-w-0 flex-1">
              <div className="text-sm font-semibold">{row.title}</div>
              <div className="text-xs text-muted-foreground mt-0.5">{row.desc}</div>
            </div>
            <span className="relative inline-flex h-5 w-9 flex-none mt-0.5">
              <input
                type="checkbox"
                className="sr-only peer"
                checked={!!prefs[row.key]}
                disabled={busy || blocked}
                onChange={() => toggle(row.key)}
                data-testid={`email-pref-${row.key}-checkbox`}
              />
              <span className="absolute inset-0 rounded-full bg-muted peer-checked:bg-primary transition-colors" />
              <span className="absolute top-0.5 left-0.5 h-4 w-4 rounded-full bg-card shadow transition-transform peer-checked:translate-x-4" />
            </span>
          </label>
        ))}
      </div>
    </section>
  );
};

export default EmailPrefsCard;
